import type { Board } from "../engine/board";
import type { Game } from "../engine/game";
import type { XY } from "../engine/xy";
import type { GameSession } from "../main";
import { UiState } from "./uiState";
import { PlayerCommand, getShootCommandForDirection, getMoveCommandForDirection } from "../engine/playerCommand";
import type { AnimationLoader } from "./animationLoader";
import type { DrawableManager } from "./drawableManager";
import type { Camera } from "./camera";
import type { SpriteRenderer } from "./spriteRenderer";
import type { SoundManager } from "./sound/soundManager";
import { routeSoundBucket } from "./sound/soundRouter";
import { playUiSound, UiSound } from "./sound/uiSoundPlayer";
import type { MusicPlayer } from "./sound/musicPlayer";
import type { UserSettings } from "./userSettings";
import { HudRenderer, WEAPON_BAR_HEIGHT, HEALTH_BAR_HEIGHT } from "./hud";
import { InventoryPanelRenderer } from "./inventoryPanel";
import { LevelLabelRenderer } from "./levelLabelRenderer";
import { LevelTransitionOverlay } from "./levelTransitionOverlay";
import { LootPopupRenderer } from "./lootPopupRenderer";
import { GoldPopupRenderer } from "./goldPopupRenderer";
import { TextPopupRenderer } from "./textPopupRenderer";
import { HudMessageRenderer } from "./hudMessageRenderer";
import { HUD_MESSAGE_HINT_DURATION_MS } from "./hudMessageConstants";
import { saveSeenHints } from "./hintStorage";
import { recordLevelReached } from "./progressStorage";
import { getNewlyUnlockedCharacters } from "../engine/characterDefs";
import { SignBubbleRenderer } from "./signBubbleRenderer";
import { FloatingTextRenderer } from "./floatingTextRenderer";
import { ParticleRenderer } from "./particles/particleRenderer";
import { UnboundedEffectRenderer } from "./unboundedEffectRenderer";
import type { CanvasLayer, FrameContext } from "./canvasLayer";
import { TurnPhase } from "./turnPhase";
import { VendingMachineOverlay } from "./vendingMachineOverlay";
import { DevPerfOverlay, type FrameTiming } from "./devPerfOverlay";
import { getDevPanel } from "./devPanel";
import { DamageFlashEffect } from "./damageFlashEffect";
import { LowHealthVignette } from "./lowHealthVignette";
import { hideDarknessOverlay, renderDarknessOverlay } from "./darknessOverlay";
import { ResizeController } from "./resizeController";
import { PauseController } from "./pauseController";
import { PlayerVisualStateProvider } from "./playerVisualState";
import { dpr } from "./dpr";
import * as TileImageLoader from "./tileImageLoader";
import { Timing } from "../engine/effects/effect";
import { BallisticMovementEffect } from "../engine/effects/ballisticMovementEffect";
import { MeleeHitMovementEffect } from "../engine/effects/meleeHitMovementEffect";
import { findEntityMovementEffect } from "./movementInterpolation";
import { Sound } from "../engine/things/sounds/sound";
import { LootPickupEffect } from "../engine/effects/lootPickupEffect";
import { GoldPickupEffect } from "../engine/effects/goldPickupEffect";
import { TextPopupEffect } from "../engine/effects/textPopupEffect";
import { ParticleEffect } from "../engine/effects/particleEffect";
import { XYEffect } from "../engine/effects/xyEffect";
import { isParticleEmitter } from "../engine/interfaces";
import { resolveEntityBoardPosition } from "./particles/entityPositionResolver";
import type { PhysicalThing } from "../engine/physicalThing";
import type { ParticleType } from "../engine/particleType";
import { Text } from "../engine/things/text";
import { EventType } from "../engine/events/eventType";
import { EventParameter } from "../engine/events/eventParameter";
import { Settings } from "../settings";
import { SAVE_KEY } from "../gameFactory";
import { directionDelta, directionFromVector } from "../engine/direction";
import { getPointedDirection } from "./mouseUtil";
import { showGameOverOverlay, showVictoryOverlay, showCrashOverlay } from "./overlays";
import { setupInput } from "./input";
import { shouldRepeatHeldCommand } from "./heldKeyTracker";
import { ZOrder } from "../engine/zOrder";
import { Strings } from "../engine/strings";
import { computeCanvasLayout, type CanvasInsets } from "./canvasLayout";
import { setContextProvider, trackError } from "../telemetry/telemetry";
import { buildRunContext, trackLevelEnter, trackRunEnd, trackRunSuspendQuit } from "../telemetry/runPayloads";

const SIDEBAR_WIDTH = 213;
const CANVAS_BOTTOM_MARGIN = 4;
const CANVAS_INSETS: CanvasInsets = {
	sidebarWidth: SIDEBAR_WIDTH,
	weaponBarHeight: WEAPON_BAR_HEIGHT,
	healthBarHeight: HEALTH_BAR_HEIGHT,
	canvasBottomMargin: CANVAS_BOTTOM_MARGIN,
};

const RUSH_MULTIPLIER = 7;
// Tap-vs-hold threshold: a key must stay down this long (measured from keydown) before its first
// auto-repeat. Set above the 300ms move animation so it's the binding constraint at every animation
// speed — a quick tap stays one turn whether or not the animation is sped up.
const HELD_REPEAT_INITIAL_DELAY_MS = 350;
// Min ms between auto-repeated held turns once repeating. Under the 300ms move animation, so held
// movement at normal speed is paced by the animation; it only bites a turn with no animation to pace
// it (held WAIT, or holding into a wall) — capping the rate so it doesn't fire every frame.
const HELD_REPEAT_INTERVAL_MS = 100;
const PARTICLE_TARGET_FPS = 60;
const PARTICLE_INTERVAL_MS = 1000 / PARTICLE_TARGET_FPS;

/** currentAssetsDir getter/setter pair lets main-menu and pause-menu tileset switches share the same authoritative value. */
export interface GameSessionDeps {
	game: Game;
	drawableManager: DrawableManager;
	camera: Camera;
	spriteRenderer: SpriteRenderer;
	animationLoader: AnimationLoader;
	audioContext: AudioContext;
	soundManager: SoundManager;
	musicPlayer: MusicPlayer;
	userSettings: UserSettings;
	onQuitToMenu: (session: GameSession) => void;
	getCurrentAssetsDir: () => string;
	setCurrentAssetsDir: (dir: string) => void;
}

export class GameSessionImpl implements GameSession {
	private readonly game: Game;
	private readonly drawableManager: DrawableManager;
	private readonly camera: Camera;
	private readonly spriteRenderer: SpriteRenderer;
	private readonly animationLoader: AnimationLoader;
	private readonly audioContext: AudioContext;
	private readonly soundManager: SoundManager;
	private readonly musicPlayer: MusicPlayer;
	private readonly userSettings: UserSettings;
	private readonly onQuitToMenu: (session: GameSession) => void;
	private readonly getCurrentAssetsDir: () => string;
	private readonly setCurrentAssetsDir: (dir: string) => void;

	private readonly canvas: HTMLCanvasElement;
	private readonly ctx: CanvasRenderingContext2D;
	private readonly sidebar: HTMLElement;
	private readonly weaponBar: HTMLElement;
	private readonly healthBar: HTMLElement;
	private readonly inspectDiv: HTMLElement;

	private readonly hudRenderer: HudRenderer;
	private readonly inventoryPanel: InventoryPanelRenderer;
	private readonly levelLabel: LevelLabelRenderer;
	private readonly prePhase: TurnPhase;
	private readonly posPhase: TurnPhase;
	private readonly transitionOverlay: LevelTransitionOverlay;
	private readonly lootPopupRenderer: LootPopupRenderer;
	private readonly goldPopupRenderer: GoldPopupRenderer;
	private readonly textPopupRenderer: TextPopupRenderer;
	private readonly hudMessageRenderer: HudMessageRenderer;
	private readonly signBubbleRenderer: SignBubbleRenderer;
	private readonly floatingTextRenderer: FloatingTextRenderer;
	private readonly particleRenderer: ParticleRenderer;
	private readonly unboundedEffectRenderer: UnboundedEffectRenderer;
	// Ordered z-list of self-contained overlays. renderFrame walks this in order (interleaving
	// the heterogeneous explicit calls — sprite, mouse, damage, darkness, floating text, devperf —
	// at their documented slots). clearTransientLayers iterates it as the single reset entry point.
	private readonly transientLayers: CanvasLayer[];

	private uiState: UiState = UiState.WAITING_INPUT;
	private commandQueue: PlayerCommand[] = [];
	private heldCommand: PlayerCommand | null = null;
	// Timestamp of the keydown that began the current hold; resets only on a fresh press from rest,
	// so steering mid-hold keeps flowing. Drives the initial-delay gate in the held-repeat branch.
	private heldCommandStartTime: number = 0;
	private lastInputTurnTime: number = 0;
	private rushAnimation: boolean = false;

	private effectsLoaded: boolean = false;

	private boardSyncPending: Promise<void> | null = null;
	private boardSyncDone: boolean = false;
	private lastSyncedBoard: Board;
	private currentlyDrawnBoard: Board;

	// Cached mouse-overlay images — replaced on resize / tileset switch.
	private dashedSquareImg: HTMLCanvasElement;
	private aimRedImg: HTMLCanvasElement;
	private particleAccumulatedMs: number = 0;

	private readonly damageFlash = new DamageFlashEffect();
	private readonly lowHealthVignette = new LowHealthVignette();

	private hoveredBoardXY: XY | null = null;
	private isShiftHeld: boolean = false;
	private mousePixelX: number = 0;
	private mousePixelY: number = 0;

	private gameOverShown: boolean = false;
	private gameWonNotified: boolean = false;
	private lastLevelNumber: number;

	private readonly pauseController: PauseController;
	private vendingOverlay: VendingMachineOverlay | null = null;

	// render() self-gates on isDevMode() so the loop doesn't need to branch.
	private readonly devPerfOverlay = new DevPerfOverlay();

	private readonly resizeController: ResizeController;

	private rafId: number = 0;
	private stopped: boolean = false;
	private lastFrameTime: number | null = null;

	private inputCleanup: () => void = () => {};

	private constructor(params: {
		deps: GameSessionDeps;
		canvas: HTMLCanvasElement;
		ctx: CanvasRenderingContext2D;
		sidebar: HTMLElement;
		weaponBar: HTMLElement;
		healthBar: HTMLElement;
		inspectDiv: HTMLElement;
		hudRenderer: HudRenderer;
		inventoryPanel: InventoryPanelRenderer;
		levelLabel: LevelLabelRenderer;
		transitionOverlay: LevelTransitionOverlay;
		lootPopupRenderer: LootPopupRenderer;
		goldPopupRenderer: GoldPopupRenderer;
		textPopupRenderer: TextPopupRenderer;
		hudMessageRenderer: HudMessageRenderer;
		signBubbleRenderer: SignBubbleRenderer;
		floatingTextRenderer: FloatingTextRenderer;
		particleRenderer: ParticleRenderer;
		dashedSquareImg: HTMLCanvasElement;
		aimRedImg: HTMLCanvasElement;
	}) {
		const { deps } = params;
		this.game = deps.game;
		getDevPanel()?.setCurrentSeed(this.game.seed);
		getDevPanel()?.attachPlayer(this.game.player!);
		this.gameWonNotified = deps.game.gameFlags.gameWon;
		this.drawableManager = deps.drawableManager;
		this.camera = deps.camera;
		this.spriteRenderer = deps.spriteRenderer;
		this.animationLoader = deps.animationLoader;
		this.audioContext = deps.audioContext;
		this.soundManager = deps.soundManager;
		this.musicPlayer = deps.musicPlayer;
		this.userSettings = deps.userSettings;
		this.onQuitToMenu = deps.onQuitToMenu;
		this.getCurrentAssetsDir = deps.getCurrentAssetsDir;
		this.setCurrentAssetsDir = deps.setCurrentAssetsDir;

		this.canvas = params.canvas;
		this.ctx = params.ctx;
		this.sidebar = params.sidebar;
		this.weaponBar = params.weaponBar;
		this.healthBar = params.healthBar;
		this.inspectDiv = params.inspectDiv;

		this.hudRenderer = params.hudRenderer;
		this.inventoryPanel = params.inventoryPanel;
		this.levelLabel = params.levelLabel;
		this.prePhase = new TurnPhase(this.animationLoader);
		this.posPhase = new TurnPhase(this.animationLoader);
		this.transitionOverlay = params.transitionOverlay;
		this.lootPopupRenderer = params.lootPopupRenderer;
		this.goldPopupRenderer = params.goldPopupRenderer;
		this.textPopupRenderer = params.textPopupRenderer;
		this.hudMessageRenderer = params.hudMessageRenderer;
		this.signBubbleRenderer = params.signBubbleRenderer;
		this.floatingTextRenderer = params.floatingTextRenderer;
		this.particleRenderer = params.particleRenderer;
		this.unboundedEffectRenderer = new UnboundedEffectRenderer();

		// z-order from docs/ui-architecture.md "Render order". Heterogeneous renderers
		// (sprite, mouse, damage, darkness, floating text, devperf) are interleaved as
		// explicit calls in renderFrame at their documented positions.
		this.transientLayers = [
			this.unboundedEffectRenderer,
			this.particleRenderer,
			this.lootPopupRenderer,
			this.goldPopupRenderer,
			this.textPopupRenderer,
			this.hudMessageRenderer,
			this.signBubbleRenderer,
			this.transitionOverlay,
		];

		this.dashedSquareImg = params.dashedSquareImg;
		this.aimRedImg = params.aimRedImg;

		this.lastSyncedBoard = deps.game.getCurrentBoard();
		this.currentlyDrawnBoard = deps.game.getCurrentBoard();
		this.lastLevelNumber = deps.game.currentLevelNumber;
		this.recordProgressAndAnnounceUnlocks();
		setContextProvider(() => buildRunContext(this.game));

		this.resizeController = new ResizeController({
			canvas: this.canvas,
			camera: this.camera,
			animationLoader: this.animationLoader,
			drawableManager: this.drawableManager,
			spriteRenderer: this.spriteRenderer,
			game: this.game,
			userSettings: this.userSettings,
			sidebarWidth: SIDEBAR_WIDTH,
			weaponBarHeight: WEAPON_BAR_HEIGHT,
			healthBarHeight: HEALTH_BAR_HEIGHT,
			canvasBottomMargin: CANVAS_BOTTOM_MARGIN,
			onTileImagesRefreshed: (dashed, aim) => {
				this.dashedSquareImg = dashed;
				this.aimRedImg = aim;
			},
		});

		this.pauseController = new PauseController({
			game: this.game,
			userSettings: this.userSettings,
			soundManager: this.soundManager,
			musicPlayer: this.musicPlayer,
			camera: this.camera,
			spriteRenderer: this.spriteRenderer,
			animationLoader: this.animationLoader,
			drawableManager: this.drawableManager,
			hudRenderer: this.hudRenderer,
			inventoryPanel: this.inventoryPanel,
			hudMessageRenderer: this.hudMessageRenderer,
			getCurrentAssetsDir: this.getCurrentAssetsDir,
			setCurrentAssetsDir: this.setCurrentAssetsDir,
			onTileImagesRefreshed: (d, a) => {
				this.dashedSquareImg = d;
				this.aimRedImg = a;
			},
			onLayoutChange: () => this.resizeController.applyLayout(),
			onQuit: () => this.quitToMenu(),
		});
	}

	static async create(deps: GameSessionDeps): Promise<GameSessionImpl> {
		const { game, drawableManager, camera, spriteRenderer, animationLoader, userSettings } = deps;

		// Two-phase transitions let the iris animate around the level swap.
		game.autoCompleteTransitions = false;

		let sessionRef: GameSessionImpl | null = null;
		animationLoader.setPlayerVisualStateProvider(
			new PlayerVisualStateProvider(game, () => sessionRef?.isPlayerWalking() ?? false),
		);

		const canvas = document.getElementById("game") as HTMLCanvasElement;
		const ctx = canvas.getContext("2d")!;
		const gameArea = document.getElementById("game-area")!;
		const inspectDiv = document.getElementById("inspect-text")!;
		const sidebar = document.getElementById("hud-sidebar")!;
		const weaponBar = document.getElementById("weapon-bar")!;
		const healthBar = document.getElementById("health-bar")!;

		const ratio = dpr();
		const board = game.getCurrentBoard();
		const { cssWidth, cssHeight, cellSize } = computeCanvasLayout(
			board.width,
			board.height,
			ratio,
			CANVAS_INSETS,
			!userSettings.cameraFollowPlayer,
		);
		canvas.style.width = cssWidth + "px";
		canvas.style.height = cssHeight + "px";
		gameArea.style.width = cssWidth + "px";
		canvas.width = Math.round(cssWidth * ratio);
		canvas.height = Math.round(cssHeight * ratio);

		camera.setCellSize(cellSize, cellSize);
		animationLoader.setCellSize(cellSize, cellSize);

		await spriteRenderer.loadFloorTile();
		const dashedSquareImg = await TileImageLoader.getImage("DashedSquare.png", camera.cellWidth, camera.cellHeight);
		const aimRedImg = await TileImageLoader.getImage("AimRed.png", camera.cellWidth, camera.cellHeight);

		camera.setViewportSize(canvas.width, canvas.height);
		if (userSettings.cameraFollowPlayer) {
			spriteRenderer.setFollowTarget(game.player!);
			camera.centerOn(game.player!.xy);
		} else {
			spriteRenderer.setFollowTarget(null);
			camera.centerOnBoard(board.width, board.height);
		}
		await drawableManager.syncWithBoard(board);
		await spriteRenderer.syncFloor(game.getCurrentBoard());

		sidebar.innerHTML = "";
		weaponBar.innerHTML = "";
		healthBar.innerHTML = "";

		// Order matters: sidebar children render top-to-bottom — level label sits above inventory, event log at the bottom.
		const queueUseItem = (itemKey: string): void => {
			if (sessionRef) sessionRef.queueUseItemCommand(itemKey);
		};
		const levelLabel = new LevelLabelRenderer(sidebar);
		levelLabel.update(board.areaName);
		const inventoryPanel = new InventoryPanelRenderer(sidebar, game.player!, queueUseItem);

		const hudRenderer = new HudRenderer(sidebar, weaponBar, healthBar, game.player!);
		hudRenderer.update();

		const transitionOverlay = new LevelTransitionOverlay();
		const lootPopupRenderer = new LootPopupRenderer();
		const goldPopupRenderer = new GoldPopupRenderer();
		const textPopupRenderer = new TextPopupRenderer();
		const hudMessageRenderer = new HudMessageRenderer();
		const signBubbleRenderer = new SignBubbleRenderer();
		const floatingTextRenderer = new FloatingTextRenderer();
		const particleRenderer = new ParticleRenderer();

		const session = new GameSessionImpl({
			deps,
			canvas,
			ctx,
			sidebar,
			weaponBar,
			healthBar,
			inspectDiv,
			hudRenderer,
			inventoryPanel,
			levelLabel,
			transitionOverlay,
			lootPopupRenderer,
			goldPopupRenderer,
			textPopupRenderer,
			hudMessageRenderer,
			signBubbleRenderer,
			floatingTextRenderer,
			particleRenderer,
			dashedSquareImg,
			aimRedImg,
		});
		sessionRef = session;
		inventoryPanel.update();

		// Initial generator sync so particles emit before the first turn.
		session.syncParticleGenerators();

		session.attachListeners();
		deps.musicPlayer.setTrack(board.resolveMusicId());
		playUiSound(UiSound.GAME_START);
		session.start();
		return session;
	}

	// Arrow fields so `this` binds and the reference can be passed to removeEventListener.
	private mouseMoveHandler = (event: MouseEvent): void => {
		const ratio = dpr();
		const boardXY = this.camera.pixelToBoard(event.offsetX * ratio, event.offsetY * ratio);
		this.hoveredBoardXY = this.game.getCurrentBoard().isInBounds(boardXY) ? boardXY : null;
		this.isShiftHeld = event.shiftKey;
		this.mousePixelX = event.offsetX * ratio;
		this.mousePixelY = event.offsetY * ratio;
		this.canvas.style.cursor = this.isShiftHeld ? "crosshair" : "default";

		if (this.hoveredBoardXY === null) {
			this.inspectDiv.textContent = "";
			return;
		}
		const things = this.game
			.getCurrentBoard()
			.getThingsAt(this.hoveredBoardXY)
			.filter((t) => t.isVisible() && t.getZOrder() > ZOrder.FLOOR)
			.sort((a, b) => b.getZOrder() - a.getZOrder());
		const names = things.map((t) => t.getName()).filter((n) => n.length > 0);
		if (names.length === 0) {
			this.inspectDiv.textContent = "";
		} else {
			let text = (Strings.get("ui_yousee") ?? "You see:") + " " + names[0];
			if (names.length > 1) {
				text += "  —  " + (Strings.get("ui_youalsosee") ?? "You also see:") + " " + names[1];
				for (let i = 2; i < names.length; i++) {
					text += ", " + names[i];
				}
			}
			this.inspectDiv.textContent = text;
		}
	};

	private mouseDownHandler = (event: MouseEvent): void => {
		if (event.button !== 0) return;
		// Modal click falls through to normal handling — mirrors input.ts's keyboard interception.
		if (this.signBubbleRenderer.isOpen()) {
			this.signBubbleRenderer.dismiss();
		}
		const ratio = dpr();
		const boardXY = this.camera.pixelToBoard(event.offsetX * ratio, event.offsetY * ratio);
		if (!this.game.getCurrentBoard().isInBounds(boardXY)) return;

		if (event.shiftKey) {
			this.game.player!.setAutoMoving(false);
			const playerCenter = this.camera.getCellCenterPixel(this.game.player!.xy);
			const dir = getPointedDirection(event.offsetX * ratio, event.offsetY * ratio, playerCenter.x, playerCenter.y);
			const cmd = getShootCommandForDirection(dir);
			if (this.uiState !== UiState.WAITING_INPUT) {
				this.rushAnimation = true;
			}
			if (this.commandQueue.length < 1) {
				this.commandQueue.push(cmd);
			}
			return;
		}

		// Adjacent click — direct move, no pathfinding / danger check.
		const delta = boardXY.subtract(this.game.player!.xy);
		if (Math.abs(delta.x) <= 1 && Math.abs(delta.y) <= 1 && !(delta.x === 0 && delta.y === 0)) {
			const dir = directionFromVector(delta);
			if (dir !== null) {
				this.game.player!.setAutoMoving(false);
				if (this.uiState !== UiState.WAITING_INPUT) {
					this.rushAnimation = true;
				}
				if (this.commandQueue.length < 1) {
					this.commandQueue.push(getMoveCommandForDirection(dir));
				}
				return;
			}
		}

		// Distant click — auto-move to target.
		const distanceMap = this.game.getCurrentBoard().getDistanceMapToPlayerIncludingDoors();
		if (distanceMap) {
			this.game.autoMoveTo(boardXY, distanceMap);
			if (!this.game.isAutoMoving()) {
				this.afterTurnRun();
				this.game.getEvents().length = 0;
			} else if (event.detail >= 2) {
				this.rushAnimation = true;
			}
		}
	};

	private mouseLeaveHandler = (): void => {
		this.hoveredBoardXY = null;
		this.inspectDiv.textContent = "";
		this.canvas.style.cursor = "default";
	};

	private quitToMenu(): void {
		trackRunSuspendQuit(this.game);
		this.onQuitToMenu(this);
	}

	private trackLevelEnterIfChanged(): void {
		if (this.game.currentLevelNumber === this.lastLevelNumber) return;
		this.lastLevelNumber = this.game.currentLevelNumber;
		trackLevelEnter(this.game);
		this.recordProgressAndAnnounceUnlocks();
	}

	private recordProgressAndAnnounceUnlocks(): void {
		if (this.game.isTutorial) return;
		const { previousMax, newMax } = recordLevelReached(this.game.currentLevelNumber);
		for (const def of getNewlyUnlockedCharacters(previousMax, newMax)) {
			const name = Strings.get(def.nameKey) ?? def.id;
			this.hudMessageRenderer.showAnnouncement("hud_characterUnlocked", [name], HUD_MESSAGE_HINT_DURATION_MS);
		}
	}

	/** Safe to call multiple times. */
	stop(): void {
		this.stopped = true;
		setContextProvider(null);
		this.animationLoader.setPlayerVisualStateProvider(null);
		cancelAnimationFrame(this.rafId);
		this.inputCleanup();
		this.soundManager.clear();
		this.musicPlayer.setTrack(null);
		this.sidebar.innerHTML = "";
		this.inspectDiv.textContent = "";
		this.canvas.removeEventListener("mousemove", this.mouseMoveHandler);
		this.canvas.removeEventListener("mousedown", this.mouseDownHandler);
		this.canvas.removeEventListener("mouseleave", this.mouseLeaveHandler);
		this.resizeController.dispose();
		this.pauseController.dismiss();
		this.lowHealthVignette.dispose();
		this.devPerfOverlay.dispose();
		getDevPanel()?.detachPlayer();
		hideDarknessOverlay();
	}

	private attachListeners(): void {
		this.canvas.addEventListener("mousemove", this.mouseMoveHandler);
		this.canvas.addEventListener("mousedown", this.mouseDownHandler);
		this.canvas.addEventListener("mouseleave", this.mouseLeaveHandler);
		this.resizeController.attach();

		this.inputCleanup = setupInput(
			this.game.player!,
			(cmd: PlayerCommand) => {
				this.audioContext.resume();
				this.musicPlayer.tryStart();
				if (this.uiState !== UiState.WAITING_INPUT) {
					this.rushAnimation = true;
				}
				if (this.commandQueue.length < 1) {
					this.commandQueue.push(cmd);
				}
			},
			() => {
				playUiSound(UiSound.WEAPON_SWITCH);
				this.hudRenderer.update();
				this.inventoryPanel.update();
			},
			() => this.pauseController.toggle(),
			() => this.signBubbleRenderer.isOpen(),
			() => this.signBubbleRenderer.dismiss(),
			() => this.vendingOverlay?.isOpen() ?? false,
			() => this.vendingOverlay?.dismiss(),
			(cmd: PlayerCommand | null) => {
				// Fresh press from rest stamps the hold's start; steering mid-hold (non-null → non-null)
				// keeps the original time so a direction change doesn't re-impose the initial delay.
				if (cmd !== null && this.heldCommand === null) {
					this.heldCommandStartTime = performance.now();
				}
				this.heldCommand = cmd;
			},
		);
	}

	private start(): void {
		this.rafId = requestAnimationFrame(this.loop);
	}

	queueUseItemCommand(itemKey: string): void {
		this.game.player!.setUseItemCommand(itemKey);
		if (this.commandQueue.length < 1) {
			this.commandQueue.push(PlayerCommand.USE_ITEM);
		}
	}

	private syncParticleGenerators(): void {
		const emitters: { entity: PhysicalThing; type: ParticleType }[] = [];
		for (const thing of this.game.getCurrentBoard().getPhysicalThings()) {
			if (isParticleEmitter(thing)) {
				const type = thing.getParticleType();
				if (type !== null) {
					emitters.push({ entity: thing as PhysicalThing, type });
				}
			}
		}
		this.particleRenderer.syncGenerators(emitters);
	}

	private afterTurnRun(): void {
		if (!this.game.isAutoMoving()) this.rushAnimation = false;
		this.trackLevelEnterIfChanged();
		this.hudRenderer.update();
		this.inventoryPanel.update();
		this.levelLabel.update(this.game.getCurrentBoard().areaName);
		const turnEvents = this.game.getEvents();
		this.hudRenderer.processEvents(turnEvents);
		for (const event of turnEvents) {
			if (event.type === EventType.REACHED_AREA) {
				const areaName = event.getParameter(EventParameter.AREA_NAME) ?? "???";
				this.hudMessageRenderer.showAnnouncement("hud_reachedArea", [areaName]);
			}
			if (event.type === EventType.POWER_PLANT_ACTIVATED) {
				this.hudMessageRenderer.showAnnouncement("hud_powerPlantActivated");
			}
			if (event.type === EventType.POWER_PLANT_GATE_NO_POWER) {
				this.hudMessageRenderer.showAnnouncement("hud_powerPlantGate_noPower");
			}
			if (event.type === EventType.POWER_PLANT_GATE_NO_KEYCARD) {
				this.hudMessageRenderer.showAnnouncement("hud_powerPlantGate_noKeyCard");
			}
			if (event.type === EventType.POWER_PLANT_GATE_OPENED) {
				this.hudMessageRenderer.showAnnouncement("hud_powerPlantGate_opening");
			}
			if (event.type === EventType.VENDING_MACHINE_NO_POWER) {
				this.hudMessageRenderer.showAnnouncement("hud_vendingMachine_noPower");
			}
			if (event.type === EventType.TERMINAL_NO_POWER) {
				this.hudMessageRenderer.showAnnouncement("hud_terminal_noPower");
			}
			if (event.type === EventType.TERMINAL_NO_KEYCARD) {
				this.hudMessageRenderer.showAnnouncement("hud_terminal_noKeyCard");
			}
			if (event.type === EventType.FIRST_WEAPON_PICKUP) {
				const weaponNameKey = event.getParameter(EventParameter.WEAPON_NAME) ?? "";
				const familyHotkey = event.getParameter(EventParameter.FAMILY_HOTKEY) ?? "";
				const weaponName = Strings.get(weaponNameKey) ?? weaponNameKey;
				this.hudMessageRenderer.showAnnouncement(
					"hud_firstWeaponPickup",
					[weaponName, familyHotkey],
					HUD_MESSAGE_HINT_DURATION_MS,
				);
			}
			if (event.type === EventType.LOW_HP_MEDIKIT_HINT) {
				this.hudMessageRenderer.showAnnouncement("hud_lowHpMedikit", [], HUD_MESSAGE_HINT_DURATION_MS);
			}
			if (event.type === EventType.HINT) {
				const key = event.getParameter(EventParameter.HINT_STRING_KEY);
				if (key) this.hudMessageRenderer.showHint(key);
			}
		}
		this.hudMessageRenderer.setActivePrompts([...this.game.getCurrentBoard().getCurrentPrompts()]);
		const pendingSignText = this.game.getCurrentBoard().consumePendingSignText();
		if (pendingSignText !== null) {
			this.signBubbleRenderer.show(pendingSignText.xy, pendingSignText.text);
		}

		// Vending machine: open on request, close on damage, refresh otherwise.
		const vendingRequest = this.game.getCurrentBoard().consumeVendingMachineRequest();
		if (vendingRequest !== null) {
			this.commandQueue.length = 0;
			this.vendingOverlay = new VendingMachineOverlay(
				this.game.player!,
				vendingRequest,
				() => {
					this.vendingOverlay = null;
				},
				() => {
					if (this.commandQueue.length < 1) {
						this.commandQueue.push(PlayerCommand.WAIT);
					}
				},
			);
			this.vendingOverlay.show();
		} else if (this.vendingOverlay?.isOpen()) {
			if (this.game.player!.damageReceivedThisTurn > 0) {
				this.vendingOverlay.dismiss();
			} else {
				this.vendingOverlay.refresh();
			}
		}

		if (this.game.player && this.game.player.hp > 0) {
			this.game.stats.tracker.recordHpSnapshot(this.game.player.hp);
		}
		if (this.game.player!.damageReceivedThisTurn > 0) {
			this.damageFlash.trigger();
		}
		localStorage.setItem(SAVE_KEY, JSON.stringify(this.game.serialize()));
		saveSeenHints(this.game.seenHints);

		if (this.game.isGameOver() && !this.gameOverShown) {
			this.gameOverShown = true;
			trackRunEnd(this.game, "died");
			localStorage.removeItem(SAVE_KEY);
			showGameOverOverlay(this.game.seed, this.game.stats, () => this.quitToMenu());
		}

		if (this.game.gameFlags.gameWon && !this.gameWonNotified) {
			this.gameWonNotified = true;
			trackRunEnd(this.game, "won");
			showVictoryOverlay(
				this.game.seed,
				this.game.stats,
				() => {},
				() => this.quitToMenu(),
			);
		}

		this.musicPlayer.setTrack(this.game.getCurrentBoard().resolveMusicId());
	}

	// Aborted turns skip the effects pipeline for snappiness, but some aborts still need it to run —
	// otherwise their feedback is wiped at the next processTurn. Two surfaceable signals: an emitted
	// event (jetpack no-safe-spot, landmine invalid placement, medikit at full HP), or an above-head
	// text popup (e.g. the recharge popup when a Rechargeable weapon fires while cooling down, or a
	// weapon warning). We don't key off sounds — looping ones (drill, flame) would false-trigger.
	private abortedTurnHasSurfaceableFeedback(): boolean {
		if (this.game.getEvents().length > 0) return true;
		return this.game
			.getCurrentBoard()
			.getLastTurnEffects()
			.some((e) => e instanceof TextPopupEffect);
	}

	private loadTurnEffects(): void {
		this.prePhase.clear();
		this.posPhase.clear();

		// On level transition, read effects from the outgoing board and keep rendering it during pre-effects.
		const boardChanged = this.currentlyDrawnBoard !== this.game.getCurrentBoard();
		const effectSource = boardChanged ? this.currentlyDrawnBoard : this.game.getCurrentBoard();
		const effects = effectSource.getLastTurnEffects();

		const unboundedEffects = effects.filter((e) => e.getUnbounded());
		const boundedEffects = effects.filter((e) => !e.getUnbounded());

		const preEffects = boundedEffects.filter((e) => e.getTiming() === Timing.BEFORE_STATE_UPDATE);
		const posEffects = boundedEffects.filter((e) => e.getTiming() === Timing.AFTER_STATE_UPDATE);

		// Filled by the routing loop below; queued effects ride the order clock (see UnboundedEffectQueue).
		const queuedUnbounded: XYEffect[] = [];

		const preDeferredSounds: Sound[] = [];
		const posDeferredSounds: Sound[] = [];
		for (const thing of this.game.getCurrentBoard().getAllThings()) {
			if (!(thing instanceof Sound)) continue;
			const bucket = routeSoundBucket(thing);
			if (bucket === "pre") {
				preDeferredSounds.push(thing);
			} else if (bucket === "pos") {
				posDeferredSounds.push(thing);
			} else {
				this.soundManager.manageSound(thing);
			}
		}

		for (const effect of unboundedEffects) {
			if (effect instanceof LootPickupEffect) {
				this.lootPopupRenderer.addPopup(effect, this.camera);
			} else if (effect instanceof GoldPickupEffect) {
				this.goldPopupRenderer.addPopup(effect, this.camera);
			} else if (effect instanceof TextPopupEffect) {
				this.textPopupRenderer.addPopup(effect, this.camera);
			} else if (effect instanceof ParticleEffect) {
				this.particleRenderer.spawnBurst(effect.xy.x, effect.xy.y, effect.particleType, effect.count, effect.direction);
			} else if (effect instanceof XYEffect) {
				queuedUnbounded.push(effect);
			}
			// else: a non-positioned unbounded effect — nothing can render it; it falls through here.
		}
		const preQueued = queuedUnbounded.filter((e) => e.getTiming() === Timing.BEFORE_STATE_UPDATE);
		const posQueued = queuedUnbounded.filter((e) => e.getTiming() === Timing.AFTER_STATE_UPDATE);

		this.syncParticleGenerators();

		if (this.game.levelTransition) {
			this.clearTransientLayers();
			const center = this.camera.getCellCenterPixel(this.game.levelTransition.fromCenterXY);
			this.transitionOverlay.startClosing(center.x, center.y, this.camera.viewportWidth, this.camera.viewportHeight);
		}

		this.effectsLoaded = false;
		Promise.all([
			this.prePhase.load(preEffects, preDeferredSounds, preQueued, this.animationLoader),
			this.posPhase.load(posEffects, posDeferredSounds, posQueued, this.animationLoader),
			this.drawableManager.syncWithBoard(boardChanged ? this.currentlyDrawnBoard : this.game.getCurrentBoard(), false),
		]).then(() => {
			this.effectsLoaded = true;
		});

		this.uiState = UiState.LOADING_EFFECTS;
	}

	private clearTransientLayers(): void {
		for (const layer of this.transientLayers) layer.clear?.();
	}

	private resetStateOnError(): void {
		this.uiState = UiState.WAITING_INPUT;
		this.commandQueue.length = 0;
		this.prePhase.clear();
		this.posPhase.clear();
		this.transitionOverlay.stop();
		this.clearTransientLayers();
		this.soundManager.clear();
		this.boardSyncPending = null;
		this.boardSyncDone = false;
		this.effectsLoaded = false;
		this.currentlyDrawnBoard = this.game.getCurrentBoard();
		this.game.levelTransition = null;
	}

	private advancePhase(phase: TurnPhase, elapsed: number): boolean {
		phase.passTime(elapsed, this.soundManager, this.unboundedEffectRenderer);
		this.transitionOverlay.passTime(elapsed);
		if (phase.isAllExpired() && this.transitionOverlay.isComplete()) {
			phase.flush(this.soundManager, this.unboundedEffectRenderer);
			return true;
		}
		return false;
	}

	private loop = (timestamp: number): void => {
		if (this.stopped) return;

		let rawElapsed = 0;
		let elapsed = 0;
		if (this.lastFrameTime !== null) {
			rawElapsed = Math.min(timestamp - this.lastFrameTime, 100);
			elapsed = rawElapsed;
			if (this.rushAnimation) {
				elapsed *= RUSH_MULTIPLIER;
			}
			elapsed *= this.userSettings.animationSpeedFactor;
		}
		this.lastFrameTime = timestamp;

		try {
			if (this.uiState === UiState.WAITING_INPUT) {
				// Held-repeat is gated on this, NOT the top freeze-guard: vending must keep
				// processing the queue (each transaction is a turn; damage taken closes the shop).
				const vendingOpen = this.vendingOverlay?.isOpen() ?? false;
				if (this.pauseController.isPaused() || this.signBubbleRenderer.isOpen()) {
					// Paused or modal open — input.ts dismisses the modal before queueing,
					// so the next non-ESC/ENTER key flips isOpen() false and plays through.
				} else if (this.game.isGameOver()) {
					if (this.commandQueue.length > 0) this.commandQueue.shift();
				} else if (this.commandQueue.length > 0) {
					const cmd = this.commandQueue.shift()!;
					this.lastInputTurnTime = timestamp;
					this.game.player!.setCommand(cmd);
					this.game.player!.resetDamageTracking();
					this.game.processTurn();
					if (!this.game.getCurrentBoard().lastTurnAborted || this.abortedTurnHasSurfaceableFeedback()) {
						this.loadTurnEffects();
					}
				} else if (
					shouldRepeatHeldCommand({
						heldCommand: this.heldCommand,
						vendingOpen,
						now: timestamp,
						heldSince: this.heldCommandStartTime,
						lastTurnTime: this.lastInputTurnTime,
						initialDelayMs: HELD_REPEAT_INITIAL_DELAY_MS,
						intervalMs: HELD_REPEAT_INTERVAL_MS,
					})
				) {
					// Deliberately does NOT set rushAnimation: the unrushed cadence is what keeps a
					// quick tap to a single tile. Rushing held steps makes taps overstep.
					this.lastInputTurnTime = timestamp;
					this.game.player!.setCommand(this.heldCommand!);
					this.game.player!.resetDamageTracking();
					this.game.processTurn();
					if (!this.game.getCurrentBoard().lastTurnAborted || this.abortedTurnHasSurfaceableFeedback()) {
						this.loadTurnEffects();
					}
				} else if (this.game.isAutoMoving()) {
					this.game.player!.resetDamageTracking();
					this.game.followAutoMove();
					if (!this.game.getCurrentBoard().lastTurnAborted || this.abortedTurnHasSurfaceableFeedback()) {
						this.loadTurnEffects();
					}
				}
			}

			if (this.uiState === UiState.LOADING_EFFECTS) {
				if (this.effectsLoaded) {
					this.effectsLoaded = false;
					this.uiState = UiState.PROCESSING_PRE_EFFECTS;
				}
			}

			if (this.uiState === UiState.PROCESSING_PRE_EFFECTS) {
				if (this.advancePhase(this.prePhase, elapsed)) {
					// Complete transition while iris is closed so any level-gen stutter happens behind black.
					if (this.game.levelTransition && this.game.levelTransition.toCenterXY === null) {
						this.game.completeLevelTransition();
						this.trackLevelEnterIfChanged();
						// Wipe outgoing-level slashes so they can't bleed onto the new board.
						this.unboundedEffectRenderer.clear();
						this.prePhase.clearUnbounded();
						this.posPhase.clearUnbounded();
						for (const thing of this.game.getCurrentBoard().getAllThings()) {
							if (thing instanceof Sound) this.soundManager.manageSound(thing);
						}
						this.musicPlayer.setTrack(this.game.getCurrentBoard().resolveMusicId());
					}
					this.uiState = UiState.DRAWING_BOARD;
				}
			}

			if (this.uiState === UiState.DRAWING_BOARD) {
				if (this.boardSyncPending === null) {
					this.boardSyncDone = false;
					const currentBoard = this.game.getCurrentBoard();
					this.boardSyncPending = (async () => {
						await this.drawableManager.syncWithBoard(currentBoard);
						if (currentBoard !== this.lastSyncedBoard) {
							await this.spriteRenderer.syncFloor(currentBoard);
							this.lastSyncedBoard = currentBoard;
						}
						if (this.userSettings.cameraFollowPlayer) {
							this.camera.centerOn(this.game.player!.xy);
						} else {
							this.camera.centerOnBoard(currentBoard.width, currentBoard.height);
						}
					})().then(() => {
						this.boardSyncDone = true;
					});
				}
				if (this.boardSyncDone) {
					this.boardSyncDone = false;
					this.boardSyncPending = null;
					this.currentlyDrawnBoard = this.game.getCurrentBoard();

					if (this.game.levelTransition) {
						const toCenterXY = this.game.levelTransition.toCenterXY;
						if (!toCenterXY) throw new Error("levelTransition reached iris-open with toCenterXY still null");
						const center = this.camera.getCellCenterPixel(toCenterXY);
						this.transitionOverlay.startOpening(
							center.x,
							center.y,
							this.camera.viewportWidth,
							this.camera.viewportHeight,
						);
						this.game.levelTransition = null;
					}

					this.uiState = UiState.PROCESSING_POS_EFFECTS;
				}
			}

			if (this.uiState === UiState.PROCESSING_POS_EFFECTS) {
				if (this.advancePhase(this.posPhase, elapsed)) {
					this.afterTurnRun();
					this.uiState = UiState.WAITING_INPUT;
				}
			}
		} catch (err) {
			this.devPerfOverlay.noteEngineError();
			console.error(`Engine error #${this.devPerfOverlay.getEngineErrorCount()} during game loop:`, err);
			trackError(err, "crash");
			if (Settings.CONTINUE_ON_ENGINE_ERROR) {
				this.resetStateOnError();
			} else {
				this.soundManager.clear();
				showCrashOverlay(err, () => this.quitToMenu());
				return;
			}
		}

		this.damageFlash.passTime(rawElapsed);
		this.damageFlash.renderSidebarTint(this.sidebar);
		const vignettePlayer = this.game.player;
		if (vignettePlayer !== null && vignettePlayer.maxHp > 0) {
			this.lowHealthVignette.update(vignettePlayer.hp / vignettePlayer.maxHp);
		}

		// Skip render during LOADING_EFFECTS — things would draw at post-turn positions before movement
		// overrides exist — and during the DRAWING_BOARD sync wait — the post-turn state would draw before
		// its AFTER_STATE_UPDATE effects start (e.g. loot uncovered a frame before BigContainerOpening).
		let renderTiming: FrameTiming | null = null;
		if (this.uiState !== UiState.LOADING_EFFECTS && this.uiState !== UiState.DRAWING_BOARD) {
			renderTiming = this.renderFrame(elapsed, rawElapsed);
		}

		if (renderTiming !== null) {
			this.devPerfOverlay.recordFrame(rawElapsed, renderTiming);
		}

		this.soundManager.setListenerXY(this.game.player!.xy, this.game.getCurrentBoard());
		this.soundManager.playSounds();

		this.rafId = requestAnimationFrame(this.loop);
	};

	private isPlayerWalking(): boolean {
		const player = this.game.player;
		if (player === null) return false;
		const effect =
			findEntityMovementEffect(player, this.prePhase.effects) ??
			findEntityMovementEffect(player, this.posPhase.effects);
		if (effect === null) return false;
		// Jumps arc and melee lunges bounce back — walking legs read wrong on both.
		return !(effect instanceof BallisticMovementEffect) && !(effect instanceof MeleeHitMovementEffect);
	}

	/** Layer order lives in docs/ui-architecture.md. */
	private renderFrame(elapsed: number, rawElapsed: number): FrameTiming {
		const frameStartMs = performance.now();
		if (this.game.player) this.drawableManager.refreshPlayerIfStateChanged(this.game.player);
		this.drawableManager.passTime(elapsed);
		this.unboundedEffectRenderer.update(elapsed);
		this.lootPopupRenderer.passTime(rawElapsed);
		this.goldPopupRenderer.passTime(rawElapsed);
		this.textPopupRenderer.passTime(rawElapsed);
		this.hudMessageRenderer.passTime(rawElapsed);
		this.signBubbleRenderer.passTime(rawElapsed);
		this.particleAccumulatedMs += elapsed;

		const frame: FrameContext = {
			camera: this.camera,
			viewportWidth: this.camera.viewportWidth,
			viewportHeight: this.camera.viewportHeight,
			elapsed,
			rawElapsed,
		};

		const renderStartMs = performance.now();
		this.spriteRenderer.render(this.currentlyDrawnBoard, this.prePhase.effects, this.posPhase.effects);
		const renderEndMs = performance.now();

		// transientLayers[0] = unboundedEffectRenderer (z-order step 2)
		this.transientLayers[0].render(this.ctx, frame);

		let aimCellXY: XY | null = null;
		if (this.isShiftHeld && this.hoveredBoardXY !== null) {
			const playerCenter = this.camera.getCellCenterPixel(this.game.player!.xy);
			const dir = getPointedDirection(this.mousePixelX, this.mousePixelY, playerCenter.x, playerCenter.y);
			aimCellXY = this.game.player!.xy.add(directionDelta(dir));
		}
		this.spriteRenderer.renderMouseOverlays(
			this.hoveredBoardXY,
			this.isShiftHeld,
			aimCellXY,
			this.dashedSquareImg,
			this.aimRedImg,
		);

		this.damageFlash.renderCanvasOverlay(this.ctx, this.canvas);

		// Particle updates throttled to PARTICLE_TARGET_FPS; render every frame.
		if (this.particleAccumulatedMs >= PARTICLE_INTERVAL_MS) {
			this.particleRenderer.update(this.particleAccumulatedMs, (entity) =>
				resolveEntityBoardPosition(entity, this.prePhase.effects, this.posPhase.effects),
			);
			this.particleAccumulatedMs = 0;
		}
		// transientLayers[1] = particleRenderer (z-order step 5)
		this.transientLayers[1].render(this.ctx, frame);

		const darknessMs = renderDarknessOverlay(
			this.canvas,
			this.camera,
			this.currentlyDrawnBoard,
			this.prePhase.effects,
			this.posPhase.effects,
		);

		const textEntities = this.currentlyDrawnBoard.getAllThings().filter((t): t is Text => t instanceof Text);
		this.floatingTextRenderer.render(this.ctx, this.camera, textEntities);

		// transientLayers[2..6] = loot, gold, hudMessage, signBubble, transitionOverlay (z-order steps 8–11)
		for (let i = 2; i < this.transientLayers.length; i++) {
			this.transientLayers[i].render(this.ctx, frame);
		}

		const frameEndMs = performance.now();
		const frameTotalMs = frameEndMs - frameStartMs;
		const renderMs = renderEndMs - renderStartMs;
		this.devPerfOverlay.render({
			spriteRenderer: this.spriteRenderer,
			particleRenderer: this.particleRenderer,
			boardThingCount: this.game.getCurrentBoard().getAllThings().length,
		});
		return { renderMs, frameMs: frameTotalMs, darknessMs: darknessMs ?? 0 };
	}
}
